<template>
  <div>
  <DModal :isBgOverlay="true" :showClose="false" @close="closeModal">
    <div class="dto text-dark" slot="insertComponent">
      <div class="dto__header">
        <h2>Håndter versioner af opgaveoversigter</h2>
        <div class="icon__bg-circle doc-modal__close" @click="closeModal">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="dto__filters">
        <div class="dto__filters__row">
          <div class="dto__filters__field">
            <label class="d-label">Skoleår</label>
            <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
          </div>
          <div class="dto__filters__field">
            <label class="d-label">Titler</label>
            <DoFiltersFilter
              title="Titler"
              :tags="titleOptions"
              :selectedTags="selectedTitles"
              @select="toggleTitle"
              @clear="clearTitles">
            </DoFiltersFilter>
          </div>
          <div class="dto__filters__field">
            <label class="d-label">Personer</label>
            <DoFiltersFilter
              title="Personer"
              :tags="personOptions"
              :selectedTags="selectedPersons"
              @select="togglePerson"
              @clear="clearPersons">
            </DoFiltersFilter>
          </div>
          <div class="dto__filters__field">
            <label class="d-label">Grupper</label>
            <DoFiltersFilter
              :tags="tags"
              :selectedTags="selectedGroups"
              @select="toggleGroup"
              @clear="clearGroups">
            </DoFiltersFilter>
          </div>
        </div>
      </div>

      <div class="dto__body">
        <div v-if="isLoading" class="dto__loading">
          <Loader bgColor="#fff"></Loader>
        </div>
        <template v-else>
          <p v-if="rows.length < 1" class="dto__empty text-explainer">
            Der er ingen gemte versioner for dette skoleår.
          </p>
          <p v-else-if="filteredRows.length < 1" class="dto__empty text-explainer">
            Ingen versioner matcher de valgte filtre.
          </p>
          <table v-else class="dto__table">
            <thead>
              <tr>
                <th class="dto__table__check">
                  <div class="do-checkbox"
                       :class="{'do-checkbox--selected': isAllFilteredSelected}"
                       @click="toggleSelectAll"></div>
                </th>
                <th class="dto__table__sortable" @click="setSort('name')">
                  Navn <span v-if="sortKey === 'name'" class="icon-menu-open dto__table__sort-icon"
                             :class="{'dto__table__sort-icon--desc': sortDir === 'desc'}"></span>
                </th>
                <th class="dto__table__sortable" @click="setSort('title')">
                  Titel <span v-if="sortKey === 'title'" class="icon-menu-open dto__table__sort-icon"
                              :class="{'dto__table__sort-icon--desc': sortDir === 'desc'}"></span>
                </th>
                <th class="dto__table__sortable" @click="setSort('createdAt')">
                  Gemt <span v-if="sortKey === 'createdAt'" class="icon-menu-open dto__table__sort-icon"
                             :class="{'dto__table__sort-icon--desc': sortDir === 'desc'}"></span>
                </th>
                <th class="dto__table__actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sortedRows"
                  :key="row.id"
                  class="dto__table__row"
                  :class="{'dto__table__row--selected': isSelected(row.id)}"
                  @click="toggleRow(row.id)">
                <td class="dto__table__check">
                  <div class="do-checkbox"
                       :class="{'do-checkbox--selected': isSelected(row.id)}"></div>
                </td>
                <td>{{ row.name }}</td>
                <td>{{ row.title }}</td>
                <td>{{ row.createdAtFormatted }}</td>
                <td class="dto__table__actions">
                  <button class="do-bttn do-bttn--small do-bttn--warning bttn-danger" @click.stop="askDeleteRow(row)">
                    {{ $t('vue2.common.delete') }}
                  </button>
                  <button class="do-bttn do-bttn--small" @click.stop="openRow(row)">Åbn</button>
                  <button class="do-bttn do-bttn--small" @click.stop="printRow(row)">Print</button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>

      <div class="dto__footer">
        <span class="dto__footer__count">{{ selectedIds.length }} valgt</span>
        <div class="dto__footer__buttons">
          <button class="do-bttn" @click="closeModal">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--submit"
                  :class="{
                    'do-bttn--submit--disabled': selectedIds.length < 1,
                    'do-bttn--submit--loading': isLoadingPrint
                  }"
                  @click="startPrint">
            Print {{ selectedIds.length }}
          </button>
          <button class="do-bttn do-bttn--warning bttn-danger"
                  :class="{'bttn-primary--disabled': selectedIds.length < 1}"
                  @click="askDelete">
            Slet valgte
          </button>
        </div>
      </div>
    </div>
  </DModal>

    <div v-if="isPrinting" id="printSelectedTaskOverviews" class="dto-print-source" aria-hidden="true">
      <TaskOverviewUser
        v-for="row in rowsToPrint"
        :key="row.id"
        :user="userForRow(row)"
        :version="row.version"
        :isHideTaskHoursUnderThreshold="true">
      </TaskOverviewUser>
    </div>

    <DModal v-if="isDeleteWarningActive"
            :appendToBody="true"
            :isBgOverlay="true"
            :showClose="false">
      <div class="brutto-warning text-dark" slot="insertComponent">
        <div class="brutto-warning__header">
          <h2>{{ $t('vue2.common.are_you_sure_you_want_to_delete') }}?</h2>
        </div>
        <div class="brutto-warning__body">
          <svg class="tw-icon tw-icon--red" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <h4>Slet {{ pendingDeleteIds.length }} version(er)?</h4>
          <p class="text-explainer">
            {{ $t('vue2.task_versions.delete_warning_message') }}
          </p>
        </div>
        <div class="brutto-warning__footer">
          <button class="do-bttn" @click="isDeleteWarningActive = false">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--warning bttn-danger"
                  :class="[{'bttn--loading': isDeleting}]"
                  @click="deleteConfirmed">
            {{ $t('vue2.common.delete') }}
          </button>
        </div>
      </div>
    </DModal>

    <DModal v-if="activeVersion"
            :appendToBody="true"
            :isBgOverlay="true"
            :showClose="false"
            @close="activeVersion = null">
      <div class="dto-view text-dark" slot="insertComponent">
        <div class="dto-view__header">
          <h3>{{ activeUser ? activeUser.name : '' }}</h3>
          <div class="dto-view__header__right">
            <button class="do-bttn do-bttn--warning bttn-danger do-bttn--small" @click="askDeleteActive">
              <span class="icon-bin"></span> {{ $t('vue2.common.delete') }}
            </button>
            <div class="icon__bg-circle ww-modal__header__close" @click="activeVersion = null">
              <span class="icon-cross2"></span>
            </div>
          </div>
        </div>
        <div class="dto-view__body">
          <TaskOverviewUser
            v-if="activeUser"
            :user="activeUser"
            :version="activeVersion">
          </TaskOverviewUser>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import DModal from '../common/DModal'
import Loader from '../common/Loader'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import TaskOverviewUser from '../User/TaskOverview/TaskOverviewUser'
import ChangeSchoolYear from '../School/ChangeSchoolYear'

export default {
  components: {DModal, Loader, DoFiltersFilter, TaskOverviewUser, ChangeSchoolYear},
  props: {
    tags: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isLoading: false,
      isDeleting: false,
      isLoadingPrint: false,
      isPrinting: false,
      rowsToPrint: [],
      isDeleteWarningActive: false,
      versions: [],
      selectedIds: [],
      pendingDeleteIds: [],
      selectedPersons: [],
      selectedTitles: [],
      selectedGroups: [],
      sortKey: 'name',
      sortDir: 'asc',
      activeVersion: null,
      activeUser: null
    }
  },
  watch: {
    schoolYearId () {
      this.selectedIds = []
      this.pendingDeleteIds = []
      this.selectedPersons = []
      this.selectedTitles = []
      this.selectedGroups = []
      this.activeVersion = null
      this.activeUser = null
      this.readAllVersions()
    },
    filteredRows (rows) {
      const visibleIds = {}
      rows.forEach(row => {
        visibleIds[row.id] = true
      })
      this.selectedIds = this.selectedIds.filter(id => visibleIds[id])
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolYearId () {
      return this.$store.getters.schoolYearId
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    rows () {
      return this.versions
        .filter(version => version.id)
        .map(version => {
          const user = this.allUsersInHash[version.user_id]
          const title = version.title ? version.title : moment(version.created_at).format('LLL')
          return {
            id: version.id,
            userId: version.user_id,
            isKnownUser: !!user,
            name: user ? user.name : '#' + version.user_id,
            tags: user && user.user_tags ? user.user_tags : [],
            title: title,
            createdAt: version.created_at,
            createdAtFormatted: moment(version.created_at).format('LLL'),
            version: Object.assign({}, version, {vTitle: title})
          }
        })
        .sort((a, b) => {
          const byName = a.name.localeCompare(b.name)
          if (byName !== 0) {
            return byName
          }
          return new Date(b.createdAt) - new Date(a.createdAt)
        })
    },
    personOptions () {
      const names = this.rows.filter(row => row.isKnownUser).map(row => row.name)
      return Array.from(new Set(names))
    },
    titleOptions () {
      const titles = this.rows.map(row => row.title)
      return Array.from(new Set(titles))
    },
    filteredRows () {
      return this.rows.filter(row => {
        if (this.selectedPersons.length > 0 && this.selectedPersons.indexOf(row.name) < 0) {
          return false
        }
        if (this.selectedTitles.length > 0 && this.selectedTitles.indexOf(row.title) < 0) {
          return false
        }
        if (this.selectedGroups.length > 0 && !this.selectedGroups.some(group => row.tags.indexOf(group) > -1)) {
          return false
        }
        return true
      })
    },
    sortedRows () {
      const dir = this.sortDir === 'desc' ? -1 : 1
      const key = this.sortKey
      return this.filteredRows.slice().sort((a, b) => {
        if (key === 'createdAt') {
          return (new Date(a.createdAt) - new Date(b.createdAt)) * dir
        }
        return String(a[key]).localeCompare(String(b[key])) * dir
      })
    },
    isAllFilteredSelected () {
      return this.filteredRows.length > 0 && this.filteredRows.every(row => this.selectedIds.indexOf(row.id) > -1)
    },
    selectedRows () {
      return this.sortedRows.filter(row => this.selectedIds.indexOf(row.id) > -1)
    }
  },
  methods: {
    toggleGroup (group) {
      const idx = this.selectedGroups.indexOf(group)
      if (idx > -1) {
        this.selectedGroups.splice(idx, 1)
      } else {
        this.selectedGroups.push(group)
      }
    },
    clearGroups () {
      this.selectedGroups = []
    },
    togglePerson (person) {
      const idx = this.selectedPersons.indexOf(person)
      if (idx > -1) {
        this.selectedPersons.splice(idx, 1)
      } else {
        this.selectedPersons.push(person)
      }
    },
    clearPersons () {
      this.selectedPersons = []
    },
    toggleTitle (title) {
      const idx = this.selectedTitles.indexOf(title)
      if (idx > -1) {
        this.selectedTitles.splice(idx, 1)
      } else {
        this.selectedTitles.push(title)
      }
    },
    clearTitles () {
      this.selectedTitles = []
    },
    setSort (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
    },
    isSelected (id) {
      return this.selectedIds.indexOf(id) > -1
    },
    toggleRow (id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx > -1) {
        this.selectedIds.splice(idx, 1)
      } else {
        this.selectedIds.push(id)
      }
    },
    openRow (row) {
      this.activeUser = this.userForRow(row)
      this.activeVersion = row.version
    },
    userForRow (row) {
      return this.allUsersInHash[row.userId] || {
        id: row.userId,
        name: row.name,
        user_tags: row.tags
      }
    },
    printRow (row) {
      this.startPrint([row])
    },
    startPrint (rows) {
      const toPrint = Array.isArray(rows) && rows.length ? rows : this.selectedRows
      if (toPrint.length < 1 || this.isLoadingPrint) {
        return
      }
      this.rowsToPrint = toPrint
      this.isLoadingPrint = true
      this.isPrinting = true
      this.$nextTick(_ => {
        this.$nextTick(_ => {
          this.isLoadingPrint = false
          this.print()
        })
      })
    },
    print () {
      const printRoot = document.getElementById('printSelectedTaskOverviews')
      if (!printRoot) {
        this.isPrinting = false
        this.rowsToPrint = []
        return
      }
      const i18nTaskOverview = this.$t('vue2.task_versions.task_overview')
      const prtHtml = printRoot.innerHTML
      let stylesHtml = ''
      for (const node of [...document.querySelectorAll('link[rel="stylesheet"][href*="/app/static/css/"], style')]) {
        stylesHtml += node.outerHTML
      }
      const pageBreakStyles = `<style>@media print {
        #printSelectedTaskOverviews .to-user { page-break-after: always; break-after: page; }
        #printSelectedTaskOverviews .to-user:last-child { page-break-after: auto; break-after: auto; }
      }</style>`
      const WinPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0')
      if (!WinPrint) {
        this.isPrinting = false
        this.rowsToPrint = []
        return
      }
      WinPrint.document.write(`<!DOCTYPE html><html id="html-bg-white"><head><title>${i18nTaskOverview}</title>${stylesHtml}${pageBreakStyles}</head><body><div id="printSelectedTaskOverviews">${prtHtml}</div></body></html>`)
      const afterPrint = () => {
        WinPrint.close()
        this.isPrinting = false
        this.rowsToPrint = []
      }
      WinPrint.onafterprint = afterPrint
      setTimeout(_ => {
        WinPrint.document.close()
        WinPrint.focus()
        WinPrint.print()
      }, 1000)
    },
    toggleSelectAll () {
      if (this.isAllFilteredSelected) {
        const filteredIds = this.filteredRows.map(row => row.id)
        this.selectedIds = this.selectedIds.filter(id => filteredIds.indexOf(id) < 0)
      } else {
        this.filteredRows.forEach(row => {
          if (this.selectedIds.indexOf(row.id) < 0) {
            this.selectedIds.push(row.id)
          }
        })
      }
    },
    askDelete () {
      if (this.selectedIds.length < 1) {
        return
      }
      this.pendingDeleteIds = this.selectedIds.slice()
      this.isDeleteWarningActive = true
    },
    askDeleteActive () {
      if (!this.activeVersion) {
        return
      }
      this.pendingDeleteIds = [this.activeVersion.id]
      this.isDeleteWarningActive = true
    },
    askDeleteRow (row) {
      this.pendingDeleteIds = [row.id]
      this.isDeleteWarningActive = true
    },
    deleteConfirmed () {
      if (this.pendingDeleteIds.length < 1 || this.isDeleting) {
        return
      }
      this.isDeleting = true
      const ids = this.pendingDeleteIds.slice()
      const firstRow = this.rows.find(row => row.id === ids[0])
      const userId = firstRow ? firstRow.userId : null
      docendo.bulkDeleteTaskOverviews(this.schoolId, this.schoolYearId, userId, ids, success => {
        this.isDeleting = false
        this.isDeleteWarningActive = false
        if (success && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          return
        }
        this.versions = this.versions.filter(version => ids.indexOf(version.id) < 0)
        this.selectedIds = this.selectedIds.filter(id => ids.indexOf(id) < 0)
        this.pendingDeleteIds = []
        if (this.activeVersion && ids.indexOf(this.activeVersion.id) > -1) {
          this.activeVersion = null
          this.activeUser = null
        }
        eventbus.toast = {text: 'Slettet', success: true}
      }, failure => {
        this.isDeleting = false
        eventbus.toast = {text: 'Noget gik galt', success: false}
      })
    },
    closeModal () {
      this.$emit('close')
    },
    readAllVersions () {
      this.isLoading = true
      docendo.readAllTaskOverviewVersions(this.schoolId, this.schoolYearId, success => {
        this.isLoading = false
        if (success && success.status === 'error') {
          eventbus.toast = {text: success.message, success: false}
          return
        }
        this.versions = Array.isArray(success) ? success : []
      }, failure => {
        this.isLoading = false
        eventbus.toast = {text: 'Noget gik galt', success: false}
      })
    }
  },
  mounted () {
    this.readAllVersions()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.dto {
  background: #fff;
  border-radius: 4px;
  width: calc(100vw - 4rem);
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.dto__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dddddd;
}

.dto__header h2 {
  margin: 0;
}

.dto__filters {
  padding: 1rem;
  background: var(--bg-grey);
  border-bottom: 1px solid #dddddd;
}

.dto__filters__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.5rem;
}

.dto__filters__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.dto__filters__field .d-label {
  color: var(--dark-blue);
  font-size: 0.85rem;
}

.dto__body {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  min-height: 200px;
  background: var(--bg-grey);
}

.dto__loading {
  padding: 3rem 0;
}

.dto__empty {
  padding: 2rem 1rem;
  text-align: center;
}

.dto__table {
  width: 100%;
  border-collapse: collapse;
  font-weight: normal;
  background: #fff;
}

.dto__table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  text-align: left;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dddddd;
  font-size: 0.85rem;
  z-index: 1;
}

.dto__table__row {
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.15s;
}

.dto__table__row:hover {
  background: aliceblue;
}

.dto__table__row--selected {
  background: #eaf3fb;
}

.dto__table td {
  padding: 0.65rem 1rem;
}

.dto__table td:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.dto__table__check {
  width: 40px;
  text-align: center;
  border-right: none !important;
}

.dto__table__sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dto__table__sortable:hover {
  color: var(--blue);
}

.dto__table__sort-icon {
  font-size: 0.85rem;
  color: var(--blue);
  display: inline-block;
  transition: transform 0.15s;
}

.dto__table__sort-icon--desc {
  transform: rotate(180deg);
}

.dto__table__actions {
  width: 220px;
  text-align: right;
  white-space: nowrap;
}

.dto__table__actions .do-bttn + .do-bttn {
  margin-left: 0.5rem;
}

.do-bttn.do-bttn--small {
  height: 24px;
  padding: 0 0.75rem;
  line-height: 24px;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
}

.dto-view {
  background: #fff;
  border-radius: 4px;
  width: 100vw;
  max-width: 800px;
}

.dto-view__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  height: 50px;
  border-bottom: 1px solid #eee;
  border-radius: 4px 4px 0 0;
}

.dto-view__header h3 {
  margin: 0;
}

.dto-view__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dto-view__header__right .icon-bin {
  margin-right: 0.25rem;
}

.dto-view__body {
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem;
}

.dto__table__check .do-checkbox {
  margin: 0 auto;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after {
  border-radius: 1rem;
  content: '';
  display: block;
  position: absolute;
  background: #fff;
  transform: rotate(35deg);
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox.do-checkbox--selected:before {
  border-radius: 1rem;
  content: '';
  display: block;
  position: absolute;
  background: #fff;
  transform: rotate(35deg);
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.dto__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid #dddddd;
}

.dto__footer__count {
  font-size: 0.9rem;
  color: #6c7a87;
}

.dto__footer__buttons {
  display: flex;
  gap: 1rem;
}

.dto-print-source {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 800px;
}

.do-bttn.do-bttn--warning,
.bttn.bttn-danger {
  background: #ef4746;
  color: #fff;
}

.bttn-primary--disabled {
  opacity: 0.65;
  cursor: not-allowed;
  box-shadow: none !important;
}

.tw-icon.tw-icon--red {
  color: #ef4746;
  width: 24px;
}

.brutto-warning {
  width: 350px;
  border-radius: 4px;
  background: #fff;
}

.brutto-warning__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dddddd;
}

.brutto-warning__body {
  padding: 1rem;
}

.brutto-warning__footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  gap: 0.5rem;
}

.bttn.bttn-primary--small {
  padding: 2px 1rem;
}

.bttn.bttn--loading:before {
  border: 3px solid #ffffff;
  border-left: 3px solid transparent;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// DeleteTaskOverviews.vue?7ac53228